import React from "react";
import { CursorArrowRaysIcon } from "@heroicons/react/24/outline";
import {
  ControlledUseComplaintsPointsProps,
  UncontrolledUseComplaintsPointsProps,
  useComplaintsPoints,
} from "./hooks/useComplaintsPoints";
import { ComplaintsTabButton } from "./tab-button";
import TouchIcon from "./assets/touch-indicator.svg?react";
import { DetailedMap, Perspective } from "./detailed-map";
import { hasClientCursor } from "../../utils/hasClientCursor";
import { useTranslation } from "react-i18next";

type ComplaintsOverviewProps = {
  front: Perspective;
  back: Perspective;
  left: Perspective;
  right: Perspective;
} & (ControlledUseComplaintsPointsProps | UncontrolledUseComplaintsPointsProps);

const TwoLinePlaceholder: React.FC = () => (
  <div className="text-sm">
    &nbsp;
    <br />
    &nbsp;
  </div>
);

const LineBreakText: React.FC<{ text: string }> = ({ text }) => {
  return (
    <span>
      {text.split(" ").map((word, index, array) => (
        <React.Fragment key={index}>
          {word}
          {index < array.length - 1 && <br />}
        </React.Fragment>
      ))}
    </span>
  );
};

export const ComplaintsOverview: React.FC<ComplaintsOverviewProps> = ({
  front,
  back,
  left,
  right,
  value,
  onChange,
}) => {
  const clientHasCursor = hasClientCursor();
  const { t } = useTranslation();

  const {
    perspective,
    setPerspective,
    handleAddComplaintsPoint,
    complaintsPoints,
    handleRemoveComplaintsPointClick,
  } = useComplaintsPoints(
    value ? { value, onChange } : { value: undefined, onChange: undefined },
  );

  return (
    <div className="flex flex-col items-center gap-8">
      <div className="text-sm text-text-primary flex flex-row gap-2 items-center  justify-center">
        {clientHasCursor ? (
          <>
            <CursorArrowRaysIcon width={24} height={24} />
            <div className="sm:w-full w-[200px]">
              {t("pages.complaints_map.instruction.click")}
            </div>
          </>
        ) : (
          <>
            <TouchIcon width={24} height={24} />
            <div className="sm:w-full w-[200px]">
              {t("pages.complaints_map.instruction.touch")}
            </div>
          </>
        )}
      </div>
      <div className="flex flex-row md:flex-row-reverse items-center gap-10">
        <div className="flex flex-col justify-center mb-5 gap-2">
          <ComplaintsTabButton
            text={t("pages.complaints_map.front")}
            onClick={() => setPerspective("front")}
            active={perspective === "front"}
          >
            <front.SvgComponent width={32} height={70} />
          </ComplaintsTabButton>
          <ComplaintsTabButton
            text={t("pages.complaints_map.back")}
            onClick={() => setPerspective("back")}
            active={perspective === "back"}
          >
            <back.SvgComponent width={32} height={70} />
          </ComplaintsTabButton>
          <ComplaintsTabButton
            text={t("pages.complaints_map.left")}
            onClick={() => setPerspective("left")}
            active={perspective === "left"}
          >
            <left.SvgComponent width={32} height={70} />
          </ComplaintsTabButton>
          <ComplaintsTabButton
            text={t("pages.complaints_map.right")}
            onClick={() => setPerspective("right")}
            active={perspective === "right"}
          >
            <right.SvgComponent width={32} height={70} />
          </ComplaintsTabButton>
        </div>
        <div className="flex justify-center w-[200px]">
          {perspective === "front" && (
            <DetailedMap
              perspective={front}
              handleAddComplaintsPoint={handleAddComplaintsPoint("front")}
              complaintsPoints={complaintsPoints.front}
              handleRemoveComplaintsPoint={handleRemoveComplaintsPointClick(
                "front",
              )}
            >
              <div className="flex flex-row w-full justify-between text-sm text-gray-700 text-center">
                <div>
                  <LineBreakText
                    text={t("pages.complaints_map.right_indicator")}
                  />
                </div>
                <div>
                  <LineBreakText
                    text={t("pages.complaints_map.left_indicator")}
                  />
                </div>
              </div>
            </DetailedMap>
          )}
          {perspective === "back" && (
            <DetailedMap
              perspective={back}
              handleAddComplaintsPoint={handleAddComplaintsPoint("back")}
              complaintsPoints={complaintsPoints.back}
              handleRemoveComplaintsPoint={handleRemoveComplaintsPointClick(
                "back",
              )}
            >
              <div className="flex flex-row w-full justify-between text-sm text-gray-700 text-center">
                <LineBreakText
                  text={t("pages.complaints_map.left_indicator")}
                />
                <LineBreakText
                  text={t("pages.complaints_map.right_indicator")}
                />
              </div>
            </DetailedMap>
          )}
          {perspective === "left" && (
            <DetailedMap
              perspective={left}
              handleAddComplaintsPoint={handleAddComplaintsPoint("left")}
              complaintsPoints={complaintsPoints.left}
              handleRemoveComplaintsPoint={handleRemoveComplaintsPointClick(
                "left",
              )}
            >
              <TwoLinePlaceholder />
            </DetailedMap>
          )}
          {perspective === "right" && (
            <DetailedMap
              perspective={right}
              handleAddComplaintsPoint={handleAddComplaintsPoint("right")}
              complaintsPoints={complaintsPoints.right}
              handleRemoveComplaintsPoint={handleRemoveComplaintsPointClick(
                "right",
              )}
            >
              <TwoLinePlaceholder />
            </DetailedMap>
          )}
        </div>
      </div>
    </div>
  );
};
